import useUrl from 'useUrl'
import { transformVoices } from 'utils'

export function getVoicesUrl(filters, sorting) {
  const languages = filters.languages
    .map((lang) => '&language[]=' + lang)
    .join('')
    
  const demoLanguages = filters.demoLanguages
    .map((demoLang) => '&demoLanguage[]=' + demoLang)
    .join('')
    
  const demoDialects = filters.demoDialects
    .map((demoDialect) => '&demoDialect[]=' + demoDialect)
    .join('')

  const demoAccents = filters.demoAccents
    .map((demoAccent) => '&demoAccent[]=' + demoAccent)
    .join('')

  const languagesProficiency = filters.languagesProficiency
    .map((langProficiency) => '&languageProficiency[]=' + langProficiency.slug)
    .join('')

  const categories = filters.categories
    .map((category) => '&category[]=' + category)
    .join('')

  const voiceages = filters.voiceages
    .map((voiceage) => '&voiceage[]=' + voiceage)
    .join('')

  // Include non-binary genders when seaching for male or female
  // https://my.arnsbogroup.com/workgroups/group/326/tasks/task/view/83929/
  if (filters.genders.includes('male') && !filters.genders.includes('non-binary')) {
    filters.genders.push('non-binary');
  }
  if (filters.genders.includes('female') && !filters.genders.includes('non-binary')) {
    filters.genders.push('non-binary');
  }

  const genders = filters.genders
    .map((gender) => '&gender[]=' + gender)
    .join('')

  const tags = filters.tags.map((tag) => '&tags[]=' + tag).join('')

  const collections = filters.collections
    .map(({ id }) => '&collections[]=' + id)
    .join('')

  const query = filters.query
    ? `&query=${encodeURIComponent(filters.query)}`
    : ''

  // Months back ('1', '2', '3', '6') or an absolute 'YYYY-MM-DD' date.
  // Non-coordinators are clamped to a fixed window by the API.
  const newSince = filters.newSince
    ? `&new=${encodeURIComponent(filters.newSince)}`
    : ''

  const availability =
    filters.availableFrom && filters.availableTo
      ? `&from=${parseInt(filters.availableFrom.getTime()) / 1000}&to=${
          parseInt(filters.availableTo.getTime()) / 1000
        }`
      : ''

  return (
    '/rest/v2/voicetalent?x=y' +
    getPaging(sorting, filters) +
    languages +
    demoLanguages +
    demoDialects +
    demoAccents +
    languagesProficiency +
    categories +
    voiceages +
    genders +
    query +
    newSince +
    tags +
    availability +
    collections +
    getSorting(sorting)
  )
}

export function getVoices(filters, sorting) {
  const baseUrl = useUrl('en', getVoicesUrl(filters, sorting))
  return fetch(baseUrl)
    .then((response) => response.json())
    .then((data) => {
      const transformedData = {
        ...data,
        data: transformVoices(data.data),
      }

      if (baseUrl.indexOf('page=') > -1) {
        return transformedData
      } else {
        const newData = {
          data: transformedData.data.slice(
            (filters.currentPage - 1) * filters.pageSize,
            filters.currentPage * filters.pageSize
          ),
          meta: transformedData.meta,
        }
        return newData
      }
    })
}

export function getProjectVoices(project) {
  const params = new URLSearchParams()
  params.append('feature', 'project-voices')

  Object.values(project.voices)
    .map((voice) => {
      params.append('voice[]', voice.sku)
    })
    .join('&')

  const demoIds = Object.values(project.voices).flatMap((voice) => {
    return (voice.demos || []).map((demo) => parseInt(demo.id))
  })

  if (demoIds.length) {
    params.append('demos', demoIds.join(','))
  }

  let url = useUrl('en', '/rest/v2/voicetalent?' + params.toString())

  return fetch(url)
    .then((response) => response.json())
    .then(({ data }) => {
      const transformedVoices = transformVoices(data)

      return transformedVoices.reduce(
        (memo, voice) => ({
          ...memo,
          [voice.sku]: {
            ...voice,
            demos: (voice.demos || []).filter(({ id }) => demoIds.includes(id)),
          },
        }),
        {}
      )
    })
}

function getPaging(sorting, filters) {
  return '&pageSize=' + filters.pageSize + '&page=' + filters.currentPage
}

function getSorting({ field = 'displayName', direction = 'ASC' }) {
  return `&sortBy=${
    field == 'newest' ? 'created' : field
  }&sortDirection=${direction}`
}

export function getVoiceUrl(id) {
  return '/rest/v2/voicetalent?id=' + id
}

export function getVoice(id) {
  return fetch(useUrl('en', getVoiceUrl(id)))
    .then((response) => response.json())
    .then((data) => {
      if (data.data && Array.isArray(data.data)) {
        return {
          ...data,
          data: transformVoices(data.data),
        }
      }
      return data
    })
}

function getGenderSegment(gender) {
  if (gender == 'male') return '&gender[]=male'
  if (gender == 'female') return '&gender[]=female'
  if (gender == 'non-binary') return '&gender[]=non-binary'
  return ''
}

// Needs to implement correct ordering from api
export function getRecentlyUsed(language, gender) {
  const url =
    useUrl(
      'en',
      '/rest/v2/voicetalent?feature=recently-used&pageSize=8&page=1&language[]='
    ) +
    language +
    getGenderSegment(gender)
  return fetch(url)
    .then((response) => response.json())
    .then((data) => {
      if (data.data && Array.isArray(data.data)) {
        return {
          ...data,
          data: transformVoices(data.data),
        }
      }
      return data
    })
}

// Needs to implement correct ordering from api
export function getNewArrivals(language, gender) {
  let url = useUrl(
    'en',
    '/rest/v2/voicetalent?feature=new-arrivals&pageSize=8&sortBy=created&sortDirection=DESC&page=1'
  )

  if (language) {
    url = url + '&language[]=' + language
  }

  url = url + getGenderSegment(gender)

  return fetch(url)
    .then((response) => response.json())
    .then((data) => {
      if (data.data && Array.isArray(data.data)) {
        return {
          ...data,
          data: transformVoices(data.data),
        }
      }
      return data
    })
}
